// GPU view-channel tiler for spec_mod (Phase A of the GPU-resident shade).
//
// Fills spec_mod's 8 view_feat channel-planes directly in the persistent input
// gpu-buffer from a tiny 8-float uniform, replacing the ~67 MB/frame CPU→GPU
// upload of tiled constants with an ~32-byte upload + one compute dispatch.
//
// Reuses the texavatars compute-pass pattern (pipeline + uniform + bind group,
// allocated once, dispatched per frame). The bind group targets only the view
// sub-region of the spec_mod input buffer, so buf[0] maps to channel 32.

import tileViewShader from './wgsl/tile_view.wgsl?raw';

const MAX_WG = 65535; // maxComputeWorkgroupsPerDimension floor across GPUs

export class SpecViewTiler {
  constructor() {
    this.device = null;
    this.pipeline = null;
    this.uniform = null;
    this.bind = null;
    this._boundBuffer = null;
    this._stage = new ArrayBuffer(48);
    this._f32 = new Float32Array(this._stage);
    this._u32 = new Uint32Array(this._stage);
  }

  _ensure(device, inputBuffer, plane, staticCh, total) {
    if (this.device !== device || !this.pipeline) {
      this.device = device;
      this.pipeline = device.createComputePipeline({
        layout: 'auto',
        compute: {
          module: device.createShaderModule({ code: tileViewShader }),
          entryPoint: 'main',
        },
      });
      this.uniform = device.createBuffer({ size: 48, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });
      this.bind = null;
      this._boundBuffer = null;
    }
    if (this.bind === null || this._boundBuffer !== inputBuffer) {
      this.bind = device.createBindGroup({
        layout: this.pipeline.getBindGroupLayout(0),
        entries: [
          { binding: 0, resource: { buffer: this.uniform } },
          // View sub-region only: offset = 32*plane floats, size = 8*plane floats.
          { binding: 1, resource: { buffer: inputBuffer, offset: staticCh * plane * 4, size: total * 4 } },
        ],
      });
      this._boundBuffer = inputBuffer;
    }
  }

  // viewFeat: Float32Array(8). Writes the 8 view planes of inputBuffer in place.
  tile(device, inputBuffer, viewFeat, plane, staticCh, cIn) {
    const total = (cIn - staticCh) * plane;
    this._ensure(device, inputBuffer, plane, staticCh, total);

    for (let c = 0; c < 8; c++) this._f32[c] = viewFeat[c] || 0; // bytes 0..31 = vf[2]
    this._u32[8] = plane;   // byte 32
    this._u32[9] = total;   // byte 36
    device.queue.writeBuffer(this.uniform, 0, this._stage);

    const enc = device.createCommandEncoder({ label: 'hair-tile-view' });
    const pass = enc.beginComputePass();
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bind);
    pass.dispatchWorkgroups(Math.min(Math.ceil(total / 64), MAX_WG));
    pass.end();
    device.queue.submit([enc.finish()]);
  }

  dispose() {
    try { this.uniform?.destroy?.(); } catch { /* ignore */ }
    this.device = this.pipeline = this.uniform = this.bind = this._boundBuffer = null;
  }
}
